import { PayloadAction } from '@reduxjs/toolkit';
import {
  getExternalSubscriptionRequest,
  getIntpWebsiteRequest,
  getStpRatesRequest,
  setAllowUpgrade,
  setExternalWebsiteId,
  setIntpWebsite,
  setIsFirstLogin,
  setPackages,
  setTheme,
  syncAppStateAfterSubscriptionUpgradeRequest,
} from '@va/aaas/shared/actions';
import { AaasClient } from '@va/aaas/shared/api-client';
import { aaasHistory } from '@va/aaas/shared/router';
import { fetchFeaturePermissionsSaga, fetchStpRatesSaga } from '@va/aaas/shared/sagas';

import { selectExternalWebsiteId } from '@va/aaas/shared/selectors';

import { AAAS_NO_AUTH_ROUTES } from '@va/aaas/shared/constants';
import { AaasBillingMode, AdminTheme, DashboardType, GroupedPackages, IntpWebsite } from '@va/aaas/shared/types';
import { apiGateway } from '@va/api-client';
import { apiRequestResponseMsg, ONE_HOUR, websiteFlagKeys } from '@va/constants';
import {
  authenticateSucceeded,
  getPrivacySettings,
  getSsrSettings,
  updateWebsiteFlag,
} from '@va/dashboard/actions/api';
import {
  activeWebsiteSuccessullyInitialized,
  INITIALIZE,
  initializeSucceeded,
  setInstanceId,
  switchActiveWebsite,
} from '@va/dashboard/actions/app';
import {
  addNotificationV2,
  setChatwootToken,
  setDashboardType,
  setProviderName,
  setSupportCenterDomain,
} from '@va/dashboard/actions/ui';
import { accountWatchers, getWebsiteSaga, updateWebsiteLocale } from '@va/dashboard/sagas/api';
import {
  checkSsrSettings,
  fetchAndPopulateExtraWebsiteInfoSaga,
  fetchVisitLimitReachedAtSaga,
  modulePermissionsSaga,
  parseT3QueryParamsSaga,
} from '@va/dashboard/sagas/app';
import { watchers as coreWatchers } from '@va/dashboard/sagas/shared';
import { websiteRoleSelectors } from '@va/dashboard/selectors/core';
import { aiApiClient } from '@va/dashboard/shared/ai-assistant';
import { activeWebsiteSlice } from '@va/dashboard/slices';
import { appHistory } from '@va/shared/router';
import { UPDATE_WEBSITE_LOCALE_REQUEST } from '@va/standalone/shared/actions';
import { refreshToken as refreshTokenFunc } from '@va/standalone/shared/api-client';
import { AAAS_URL_PARAMS, storageItems } from '@va/standalone/shared/constants';
import {
  addTokensToLocalStorage,
  getAccessToken,
  getLastWebsiteId,
  getRefreshToken,
  getUserId,
  removeCredentialsFromLocalStorage,
} from '@va/standalone/shared/helpers';
import { UserRole, WebsiteResponse } from '@va/types/website';
import {
  applyTheme,
  getParameterByName,
  getPlatformName,
  getUrlParameterWithStorageFallback,
  LocalStorage,
  removeUrlParameter,
  setDefaultTheme,
} from '@va/util/helpers';
import { get3asWebsiteByVisaId } from 'aaas/apiClient/website';
import { addCredentialsToLocalStorage } from 'aaas/helpers/auth.helpers';
import dayjs from 'dayjs';
import flagsmith from 'flagsmith';
import { jwtDecode } from 'jwt-decode';
import { getAccountInformationSaga } from 'libs/dashboard/sagas/api/src/lib/accountWatchers';
import { isString } from 'lodash';
import moment from 'moment';
import { Task } from 'redux-saga';
import { all, call, delay, fork, join, put, select, spawn, takeLatest } from 'redux-saga/effects';
import {
  authenticateWithToken,
  impersonateTokenRefresh,
  TokenResponsePayload,
  websiteTokenRefresh,
} from '../apiClient/auth';
import { fetchExternalSubscriptionSaga } from './billingSaga';
import { initializeModals } from './modals';
import { syncAppStateAfterSubscriptionUpgrade } from './planChangeSyncSaga';

const watchers = [...coreWatchers, accountWatchers()];

function* appSagaWatchers() {
  yield all([
    takeLatest(INITIALIZE, handleInitialize),
    takeLatest(getExternalSubscriptionRequest, fetchExternalSubscriptionSaga),
    takeLatest(getStpRatesRequest, onRequestStpRates),
    takeLatest(getIntpWebsiteRequest, onRequestIntpWebsite),
    takeLatest(switchActiveWebsite, switchActiveWebsiteSaga),
    takeLatest(syncAppStateAfterSubscriptionUpgradeRequest, syncAppStateAfterSubscriptionUpgrade),
    takeLatest(UPDATE_WEBSITE_LOCALE_REQUEST, updateWebsiteLocale),
  ]);
}

function detectInitialDashboardType({
  websiteRefreshTokenParam,
  intpcTokenParam,
  localStorageRefreshToken,
}: {
  websiteRefreshTokenParam: string | null;
  intpcTokenParam: string | null;
  localStorageRefreshToken: string | null;
}): DashboardType | Error {
  if (websiteRefreshTokenParam) {
    return DashboardType.website;
  }

  if (intpcTokenParam) {
    return DashboardType.intpc;
  }

  if (localStorageRefreshToken) {
    const decodedToken = jwtDecode<{ websiteId?: string; client_id?: string; 'aaas-impersonate'?: boolean }>(
      localStorageRefreshToken,
    );
    if (decodedToken.websiteId) {
      return DashboardType.website;
    } else if (decodedToken.client_id || decodedToken['aaas-impersonate']) {
      return DashboardType.intpc;
    }
  }

  return new Error(
    `could not detect dashboard type; websiteRefreshTokenParam=${websiteRefreshTokenParam}; intpcTokenParam=${intpcTokenParam}; localStorageRefreshToken=${localStorageRefreshToken}`,
  );
}

function* handleInitialize() {
  try {
    if (AAAS_NO_AUTH_ROUTES.includes(window.location.pathname)) return;

    const allowUpgrade = getUrlParameterWithStorageFallback(AAAS_URL_PARAMS.allowUpgrade, storageItems.allowUpgrade);
    const websiteToken = getParameterByName(AAAS_URL_PARAMS.websiteToken);
    const intpcToken = getParameterByName(AAAS_URL_PARAMS.intpcToken);
    const externalWebsiteId = getUrlParameterWithStorageFallback(
      AAAS_URL_PARAMS.externalWebsiteId,
      storageItems.externalWebsiteId,
    );

    const dashboardType = detectInitialDashboardType({
      intpcTokenParam: intpcToken,
      websiteRefreshTokenParam: websiteToken,
      localStorageRefreshToken: getRefreshToken(),
    });

    if (dashboardType instanceof Error) {
      appHistory.push('/error', { title: '3as.errorPage.missingQuery.title', status: 400 });
      throw dashboardType;
    }

    let intpWebsite: IntpWebsite;

    if (dashboardType === DashboardType.website) {
      yield put(setDashboardType(DashboardType.website));
      const data: { websiteId: string } = yield authenticateWebsite(websiteToken);
      intpWebsite = yield call(get3asWebsiteByVisaId, data.websiteId);
      const updatedUrl = removeUrlParameter([
        AAAS_URL_PARAMS.intpcToken,
        AAAS_URL_PARAMS.externalWebsiteId,
        AAAS_URL_PARAMS.websiteToken,
      ]);
      window.history.replaceState(null, '', updatedUrl);
    } else {
      yield put(setDashboardType(DashboardType.intpc));

      const updatedUrl = removeUrlParameter([AAAS_URL_PARAMS.intpcToken, AAAS_URL_PARAMS.externalWebsiteId]);
      window.history.replaceState(null, '', updatedUrl);

      if (!isString(externalWebsiteId)) {
        appHistory.push('/error', { title: '3as.errorPage.missingQuery.title', status: 400 });
        throw new Error('externalWebsiteId is missing from the URL');
      }

      yield authenticateIntpc(intpcToken);
      intpWebsite = yield call(AaasClient.getWebsiteByExternalWebsiteId, externalWebsiteId);
    }

    const instanceId = intpWebsite.id;

    yield put(setExternalWebsiteId(intpWebsite.intpWebsiteId));
    yield put(setProviderName(intpWebsite.intpName));
    yield put(setIntpWebsite(intpWebsite));
    yield put(setInstanceId(instanceId));

    // Fetch standalone website , account information from php api
    const websiteCoreInfoCall = call(getWebsiteSaga);
    const accountInfoCall = call(getAccountInformationSaga);
    const extraWebsiteInfoCall = call(fetchAndPopulateExtraWebsiteInfoSaga, {
      websiteId: instanceId,
      userId: localStorage.getItem(storageItems.userId) ?? '',
    });
    const featurePermissionsCall = call(fetchFeaturePermissionsSaga);

    type allRes = [WebsiteResponse, void, void, void];

    // Using all to make the request in paralel
    const [coreWebsite]: allRes = yield all([
      websiteCoreInfoCall,
      accountInfoCall,
      extraWebsiteInfoCall,
      featurePermissionsCall,
    ]);

    yield spawn(fetchVisitLimitReachedAtSaga, { websiteId: instanceId });

    moment.tz.setDefault(coreWebsite.timezone);
    dayjs.tz.setDefault(coreWebsite.timezone);
    window.timezone = coreWebsite.timezone;

    const role: UserRole = yield select(websiteRoleSelectors.selectWebsiteRole);

    yield call(modulePermissionsSaga, role); // not doing any async actions, we can use call and wait for it

    const fetchThemeTask: Task = yield fork(fetchTheme);
    yield spawn(getPackagesSaga);

    const hasFirstLogin = coreWebsite.frontendFlags?.[websiteFlagKeys.HAD_FIRST_LOGIN];
    if (!hasFirstLogin) {
      yield put(setIsFirstLogin(true));
      yield put(updateWebsiteFlag(websiteFlagKeys.HAD_FIRST_LOGIN, true));
    }

    const fetchStpRatesTask: Task = yield fork(fetchStpRatesSaga);

    if (intpWebsite.billingMode === AaasBillingMode.PlatformManaged) {
      yield spawn(fetchExternalSubscriptionSaga);
    }

    yield put(getPrivacySettings());

    yield put(getSsrSettings());
    yield spawn(checkSsrSettings);
    if (allowUpgrade === 'true') {
      yield put(setAllowUpgrade(true));
    }
    yield spawn(parseT3QueryParamsSaga);

    // Tasks that we need to wait for before initialize is complete
    const joinedTasks: Task<any>[] = [];
    joinedTasks.push(fetchStpRatesTask); // needed in initializeModals
    joinedTasks.push(fetchThemeTask);

    // Wait for those tasks
    yield join(joinedTasks);

    yield spawn(initializeModals);

    yield put(initializeSucceeded());
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : '';
    if (errorMsg.includes(apiRequestResponseMsg.EXPIRED_TOKEN)) {
      appHistory.push('/error', { title: '3as.errorPage.expiredToken.title', status: 400, canGoBack: false });
      return;
    }

    appHistory.push('/error', { title: 'all.somethingWentWrong.title', status: 500 });
  }
}

export function* switchActiveWebsiteSaga(action: PayloadAction<{ websiteId: string }>) {
  yield put(activeWebsiteSlice.actions.setIsLoading(true));

  const newWebsiteId = action.payload.websiteId;

  yield put(setInstanceId(newWebsiteId)); // needs to be before getWebsiteSaga

  try {
    const intpWebsiteCall = call(get3asWebsiteByVisaId, newWebsiteId);
    const coreWebsiteCall = call(getWebsiteSaga);
    const extraWebsiteInfoCall = call(fetchAndPopulateExtraWebsiteInfoSaga, {
      websiteId: newWebsiteId,
      userId: localStorage.getItem(storageItems.userId) ?? '',
    });
    const featurePermissionsCall = call(fetchFeaturePermissionsSaga);

    type allRes = [Awaited<ReturnType<typeof get3asWebsiteByVisaId>>, WebsiteResponse, void, void];

    // Those calls must resolve before the dashboard can properly load
    const [intpWebsite, coreWebsite]: allRes = yield all([
      intpWebsiteCall,
      coreWebsiteCall,
      extraWebsiteInfoCall,
      featurePermissionsCall,
    ]);

    yield spawn(fetchVisitLimitReachedAtSaga, { websiteId: newWebsiteId });

    yield put(setIntpWebsite(intpWebsite));

    LocalStorage.setItem(storageItems.externalWebsiteId, intpWebsite.intpWebsiteId);
    yield put(setExternalWebsiteId(intpWebsite.intpWebsiteId));
    yield put(setProviderName(intpWebsite.intpName));

    moment.tz.setDefault(coreWebsite.timezone);
    dayjs.tz.setDefault(coreWebsite.timezone);
    window.timezone = coreWebsite.timezone;

    const role: UserRole = yield select(websiteRoleSelectors.selectWebsiteRole);

    yield call(modulePermissionsSaga, role);

    yield spawn(fetchStpRatesSaga);

    if (intpWebsite.billingMode === AaasBillingMode.PlatformManaged) {
      yield spawn(fetchExternalSubscriptionSaga);
    }

    if (role !== UserRole.DASHBOARD) {
      yield put(getPrivacySettings());
      yield put(getSsrSettings());
      yield spawn(checkSsrSettings);
    }

    yield put(activeWebsiteSuccessullyInitialized());

    aaasHistory.push('/');
  } catch (error) {
    yield put(
      addNotificationV2({
        text: 'Something went wrong!',
        autoDismiss: 2500,
        type: 'error',
      }),
    );
    console.error('onActiveWebsiteChange:', error);
    if (error instanceof Error) {
      yield put(activeWebsiteSlice.actions.setError(error.message));
    }
  } finally {
    yield put(activeWebsiteSlice.actions.setIsLoading(false));
  }
}

export function* onRequestStpRates() {
  yield spawn(fetchStpRatesSaga);
}

export function* onRequestIntpWebsite() {
  const externalWebsiteId: string = yield select(selectExternalWebsiteId);
  try {
    const website: IntpWebsite = yield call(AaasClient.getWebsiteByExternalWebsiteId, externalWebsiteId);

    yield put(setProviderName(website.intpName));
    yield put(setIntpWebsite(website));
    yield put(setInstanceId(website.id));
  } catch (error) {
    console.error('Could not fetch intp website', error);
  }
}

function* authenticateWebsite(websiteRefreshTokenParam: string | null) {
  let refreshToken = getRefreshToken();
  let websiteId = getLastWebsiteId();
  let token = getAccessToken();

  if (websiteRefreshTokenParam) {
    const response: { token: string; refreshToken: string; websiteId: string } = yield call(
      websiteTokenRefresh,
      websiteRefreshTokenParam,
    );

    token = response.token;
    refreshToken = response.refreshToken;
    websiteId = response.websiteId;
  } else if (refreshToken) {
    const response: { token: string; refreshToken: string; websiteId: string } = yield call(
      websiteTokenRefresh,
      refreshToken,
    );
    token = response.token;
    refreshToken = response.refreshToken;
    websiteId = response.websiteId;
  }

  if (!isString(token) || !isString(refreshToken)) {
    appHistory.push('/error', { title: '3as.errorPage.missingQuery.title', status: 400 });
    throw new Error('Authentication failed: website_token is missing and the fallback storage tokens are not valid');
  }

  if (websiteId) {
    const decodedToken = jwtDecode<{ company_id: string }>(token);
    flagsmith.identify(websiteId, { platform: getPlatformName(), companyId: decodedToken.company_id });
  }

  const authData = { token, refreshToken, websiteId: websiteId };

  addCredentialsToLocalStorage({
    ...authData,
  });

  AaasClient.setAuthToken(token);
  apiGateway.setAuthToken(token);
  aiApiClient.setAuthToken(token);

  yield put(authenticateSucceeded({ ...authData }));
  yield spawn(refreshWebsiteTokenPeriodically);
  return authData;
}

function* authenticateIntpc(intpcToken: string | null) {
  let token = getAccessToken();
  let refreshToken = getRefreshToken();
  let userId = getUserId();

  const decodedToken = token
    ? jwtDecode<{ websiteId?: string; company_id?: string; 'aaas-impersonate'?: boolean } | undefined>(token)
    : null;

  if (intpcToken) {
    const response: { token: string; refreshToken: string; userId: string } = yield call(
      authenticateWithToken,
      intpcToken,
    );

    token = response.token;
    refreshToken = response.refreshToken;
    userId = response.userId;
  } else if (refreshToken && !decodedToken?.['aaas-impersonate']) {
    const response: { token: string; refreshToken: string; clientId: string } = yield call(refreshTokenFunc, {
      refreshToken: refreshToken,
    });

    token = response.token;
    refreshToken = response.refreshToken;
    userId = response.clientId;
  }

  if (!isString(token) || !isString(refreshToken) || !isString(userId)) {
    appHistory.push('/error', { title: '3as.errorPage.missingQuery.title', status: 400 });
    throw new Error('Authentication failed: intpc_token is missing and the fallback storage tokens are not valid');
  }

  flagsmith.identify(userId, { platform: getPlatformName(), companyId: decodedToken?.company_id ?? '' });

  const authData = { token, refreshToken, userId };

  addCredentialsToLocalStorage({
    ...authData,
  });

  AaasClient.setAuthToken(token);
  apiGateway.setAuthToken(token);
  aiApiClient.setAuthToken(token);

  yield put(authenticateSucceeded({ ...authData }));
  yield spawn(refreshTokenPeriodically);
  return authData;
}

export default function* rootSaga() {
  yield all([...watchers, appSagaWatchers()]);
}

function* fetchTheme() {
  try {
    const theme: AdminTheme = yield call(AaasClient.getTheme);

    applyTheme({
      fontName: theme.primaryFont,
      colors: theme.colors,
    });

    const { supportCenterDomain, chatwootToken } = theme;
    yield put(setSupportCenterDomain(supportCenterDomain));
    yield put(setChatwootToken(chatwootToken));
    yield put(setTheme(theme));
  } catch (error) {
    setDefaultTheme();
  }
}

function* refreshWebsiteTokenPeriodically() {
  while (true) {
    try {
      const storedRefreshToken = getRefreshToken();
      if (!storedRefreshToken) {
        throw new Error('refresh token not found');
      }

      const response: { token: string; refreshToken: string; websiteId: string } = yield call(
        websiteTokenRefresh,
        storedRefreshToken,
      );

      addTokensToLocalStorage({ token: response.token, refreshToken: response.refreshToken });

      AaasClient.setAuthToken(response.token);
      apiGateway.setAuthToken(response.token);
      aiApiClient.setAuthToken(response.token);

      yield delay((ONE_HOUR / 2) * 1000);
    } catch (error) {
      console.error(error);
      removeCredentialsFromLocalStorage();
      return;
    }
  }
}

function* refreshTokenPeriodically(): Generator<any> {
  while (true) {
    try {
      const storedRefreshToken = getRefreshToken();
      const decodedRefreshToken = storedRefreshToken
        ? jwtDecode<{ 'aaas-impersonate'?: boolean }>(storedRefreshToken)
        : null;

      const refreshTokenRequest = decodedRefreshToken?.['aaas-impersonate']
        ? impersonateTokenRefresh
        : refreshTokenFunc;

      const response = (yield call(refreshTokenRequest, { refreshToken: storedRefreshToken })) as TokenResponsePayload;

      addTokensToLocalStorage({ token: response.token, refreshToken: response.refreshToken });

      AaasClient.setAuthToken(response.token);
      apiGateway.setAuthToken(response.token);
      aiApiClient.setAuthToken(response.token);

      LocalStorage.setItem(storageItems.clientId, response.clientId);
      LocalStorage.setItem(storageItems.companyId, response.companyId);

      yield delay((ONE_HOUR / 2) * 1000);
    } catch (error) {
      console.error(error);
      removeCredentialsFromLocalStorage();
      return;
    }
  }
}

function* getPackagesSaga() {
  try {
    const packages: GroupedPackages = yield call(AaasClient.getPackages);
    yield put(setPackages(packages));
  } catch (e) {
    console.log(e);
  }
}
